import axios from 'axios'

axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'
axios.defaults.withCredentials = true

const base = window.bookingWidget?.base

// Stop waiting for the booking API after this long so the page can show an error instead of hanging.
// A backstop for the booking API itself not answering: it gives up on Rezdy after 20s and returns an
// error, which should arrive first. Not used for availability, where a failure reads as "No times
// available", or for creating, changing or loading bookings and payments, where giving up early could
// report a booking as failed when it went through.
const TIMEOUT_MS = 30000

export default {
  availability(attraction, productCode, date, autoNext) {
    return axios
      .get(`${base}/api/availability`, {
        params: {
          attraction: attraction.id,
          date,
          product_code: productCode || '',
          auto_next: autoNext,
        },
      })
      .then((response) => response.data)
  },

  nextAvailability(attraction, date, productCode) {
    return axios
      .get(`${base}/api/availability/next`, {
        params: {
          attraction: attraction.id,
          date,
          product_code: productCode || '',
        },
      })
      .then((response) => response.data)
  },

  discounts(args) {
    return axios
      .get(`${base}/api/discounts`, { params: args })
      .then((response) => response?.data)
  },

  voucher(args) {
    return axios
      .get(`${base}/api/voucher`, { params: args, timeout: TIMEOUT_MS })
      .then((response) => response?.data)
      .catch((error) => Promise.reject(error?.response ?? error))
  },

  quote(form, signal) {
    return axios
      .post(`${base}/api/bookings/quote`, form, { timeout: TIMEOUT_MS, signal })
      .then((response) => response.data)
  },

  order(form) {
    return axios.post(`${base}/api/bookings`, form)
  },

  tmpOrder(booking, intentId) {
    return axios
      .post(`${base}/api/tmp-bookings`, {
        ...booking,
        intent_id: intentId,
      })
      .then((response) => response.data)
      .catch((error) => Promise.reject(error.response))
  },

  intents: {
    create(args) {
      return axios.post(`${base}/api/payments/intent`, args)
    },
    update(id, args) {
      return axios.post(`${base}/api/payments/intent/${id}`, args)
    },
  },

  bookings: {
    confirmPayment(paymentIntent, token) {
      return axios.post(`${base}/api/bookings/confirm`, {
        payment_intent: paymentIntent,
        token,
      }, {
        headers: { Accept: 'application/json' },
      }).then((response) => response.data)
    },
    get(bookingNumber, token) {
      return axios.get(`${base}/api/bookings/${bookingNumber}`, {
        params: { token },
      })
    },
    update(bookingNumber, token, form) {
      return axios.put(`${base}/api/bookings/${bookingNumber}`, form, {
        params: { token },
      })
    },
    moveQuote(bookingNumber, token, form) {
      return axios.post(`${base}/api/bookings/${bookingNumber}/move-quote`, form, {
        params: { token },
      })
    },
    cancel(bookingNumber, token, form) {
      return axios.put(`${base}/api/bookings/${bookingNumber}/cancel`, form, {
        params: { token },
      })
    },
  },

  upsells(args = {}) {
    const payload = {
      appear_in: args?.appear_in,
      limit: args?.limit,
      items: Array.isArray(args?.items) ? args.items : [],
    }

    if (payload.items.length > 0) {
      return axios
        .post(`${base}/api/upsells`, payload, { timeout: TIMEOUT_MS })
        .then((response) => response?.data)
    }

    return axios
      .get(`${base}/api/upsells`, {
        params: {
          appear_in: payload.appear_in,
          limit: payload.limit,
        },
        timeout: TIMEOUT_MS,
      })
      .then((response) => response?.data)
  },

  attractions(args) {
    return axios.get(`${base}/api/attractions`, { params: args, timeout: TIMEOUT_MS })
  },

  options(args) {
    return axios.get(`${base}/api/options`, { params: args, timeout: TIMEOUT_MS })
  },
}
